import { AcknowledgmentResponseStatus, BlockEvents } from 'services/socket/SocketEvents';
import { useSocketEventListener } from './useSocketEventListener';
import type { CreateBlockData } from '../UndoRedo/CreateBlockCommand';
import type { BlockAddedData, BlockEmbedAddedData, BlockTableAddedData, GridContentChangedType } from '../grid/reduxStore/saveHandlers';
import type { Block } from '../GridDndEditor/models/Block.model';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useNewBlockAddedHandler } from './useNewBlockAddedHandler';
import { useNewBlockContentChangedHandler } from './useNewBlockContentChangedHandler';
import { selectBlockContent, selectSectionIdByBlockId } from '../grid/reduxStore/editorSlice';
import type { RootState } from '../grid/reduxStore/Store';
import { useStore } from 'react-redux';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { ConsoleLogger } from '@proposify/frontend-observability';
import type { JSONContent } from '@tiptap/core';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';

export function useSocketSideEffectHandler() {
  const store = useStore<RootState>();
  const newBlockAddHandler = useNewBlockAddedHandler();
  const { handleBlockContentChangedWithoutPersisting } = useNewBlockContentChangedHandler();
  const { [FeatureFlags.aiChatSidePannel]: isAiChatSidePannelEnabled } = useFeatureFlag([FeatureFlags.aiChatSidePannel]);

  useSocketEventListener(BlockEvents.BLOCK_ADDED_SIDE_EFFECT, async (payload: BlockAddedPayload) => {
    if (!isAiChatSidePannelEnabled) return;
    const shouldPersistData = false;

    const response = await newBlockAddHandler(
      payload.sectionId,
      buildBlockData(payload.type as GridBlockType, payload),
      buildConfigData(payload),
      payload.type,
      payload.blockId,
      shouldPersistData,
      payload.userId
    );

    if (!response || response.status !== AcknowledgmentResponseStatus.OK) {
      ConsoleLogger.warn('Failed to add block from side-effect', {});
    }
  });

  useSocketEventListener(BlockEvents.BLOCK_CONTENT_CHANGED_SIDE_EFFECT, (payload: GridContentChangedType) => {
    if (!isAiChatSidePannelEnabled) return;
    const { blockId, htmlContent, userId } = payload;
    if (!(blockId && htmlContent && userId)) return;

    const state = store.getState();
    const sectionId = selectSectionIdByBlockId(state, blockId);
    if (!sectionId) return;

    const existingBlock = selectBlockContent(state, sectionId, blockId);

    if (existingBlock?.type === GridBlockType.TEXT) {
      const editor = UndoRedoManagerCommandVersion.getUndoRedoManager().getTiptapEditor(blockId);

      let parsed: JSONContent;
      try {
        parsed = JSON.parse(htmlContent);
      } catch {
        ConsoleLogger.warn(`Invalid content JSON for block ${blockId}`);
        return;
      }

      handleBlockContentChangedWithoutPersisting(blockId, sectionId, htmlContent, existingBlock.content ?? '', userId);

      if (editor && !editor.isDestroyed) {
        if (JSON.stringify(editor.getJSON()) === JSON.stringify(parsed)) return;

        editor
          .chain()
          .command(({ tr }) => {
            tr.setMeta('addToHistory', false);
            return true;
          })
          .setContent(parsed, false)
          .run();
      }
      return;
    }

    handleBlockContentChangedWithoutPersisting(blockId, sectionId, htmlContent, existingBlock?.content ?? '', userId);
  });
}

type BlockAddedPayload = BlockAddedData | BlockTableAddedData | BlockEmbedAddedData;

function buildConfigData(payload: BlockAddedPayload): Block {
  return {
    x: payload.position.left_px,
    y: payload.position.top_px,
    width: payload.dimensions.width_px,
    height: payload.dimensions.height_px,
    z: payload.zIndex ?? 0,
  };
}

function buildBlockData(blockType: GridBlockType, payload: BlockAddedPayload): CreateBlockData {
  switch (blockType) {
    case GridBlockType.TABLE:
      return { content: (payload as BlockTableAddedData).content };

    case GridBlockType.EMBED:
      return {};

    default: {
      const { htmlContent, content, styles } = payload as BlockAddedData;
      return {
        htmlContent,
        ...(content !== undefined && { content }),
        ...(styles !== undefined && { styles }),
      } as CreateBlockData;
    }
  }
}
